import React, { useState, useEffect } from 'react';
import './ProjectDescriptionSlide.css';

interface ScreenshotSet {
  label: string;
  screenshots: string[];
}

interface Project {
  category: string;
  grade: string;
  title: string;
  description: string;
  stack?: string;
  longDescription?: string;
  architecture?: string;
  color: string;
  image: string;
  screenshots?: string[];
  screenshotSets?: ScreenshotSet[];
  onClick?: () => void;
}

interface ProjectDescriptionSlideProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDescriptionSlide: React.FC<ProjectDescriptionSlideProps> = ({
  project,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeSet, setActiveSet] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
    setActiveSet(0);
  }, [project]);

  if (!project) return null;

  const sets: ScreenshotSet[] | null =
    project.screenshotSets && project.screenshotSets.length > 0
      ? project.screenshotSets
      : project.screenshots && project.screenshots.length > 0
      ? [{ label: '', screenshots: project.screenshots }]
      : null;

  const hasCarousel = sets !== null;
  const hasSetToggle = sets !== null && sets.length > 1;
  const screenshots = sets ? sets[activeSet].screenshots : [];

  const handleSetChange = (idx: number) => {
    setActiveSet(idx);
    setCurrentIndex(0);
  };

  const prev = () => setCurrentIndex(i => (i - 1 + screenshots.length) % screenshots.length);
  const next = () => setCurrentIndex(i => (i + 1) % screenshots.length);

  return (
    <>
      <div
        className={`project-slide-overlay ${isOpen ? 'project-slide-overlay--active' : ''}`}
        onClick={onClose}
      />

      <div className={`project-slide ${isOpen ? 'project-slide--open' : ''}`}>
        <button className="project-slide__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="project-slide__content">
          <div className="project-slide__header">
            <span className="project-slide__category">{project.category}</span>
            <span className="project-slide__grade">{project.grade}</span>
          </div>

          <h2 className="project-slide__title">{project.title}</h2>
          <h3 className="project-slide__subtitle">Tech Stack: {project.stack}</h3>

          {hasCarousel && (
            <>
              {hasSetToggle && (
                <div className="project-slide__set-toggle">
                  <div className="project-slide__set-toggle-track">
                    <div
                      className="project-slide__set-toggle-thumb"
                      style={{ left: `${(activeSet / (sets!.length - 1)) * 100}%`, transform: activeSet === 0 ? 'translateX(0)' : 'translateX(-100%)' }}
                    />
                    {sets!.map((s, i) => (
                      <button
                        key={s.label}
                        className={`project-slide__set-toggle-option${i === activeSet ? ' project-slide__set-toggle-option--active' : ''}`}
                        onClick={() => handleSetChange(i)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="project-slide__carousel">
                <div
                  className="project-slide__carousel-track"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {screenshots.map((src, i) => (
                    <div className="project-slide__carousel-slide" key={i}>
                      <img src={src} alt={`${project.title} screenshot ${i + 1}`} />
                    </div>
                  ))}
                </div>

                {screenshots.length > 1 && (
                  <>
                    <button
                      className="project-slide__carousel-btn project-slide__carousel-btn--prev"
                      onClick={prev}
                    >
                      ‹
                    </button>
                    <button
                      className="project-slide__carousel-btn project-slide__carousel-btn--next"
                      onClick={next}
                    >
                      ›
                    </button>
                    <div className="project-slide__carousel-dots">
                      {screenshots.map((_, i) => (
                        <button
                          key={i}
                          className={`project-slide__carousel-dot ${i === currentIndex ? 'project-slide__carousel-dot--active' : ''}`}
                          onClick={() => setCurrentIndex(i)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          <p className="project-slide__description">{project.description}</p>

          {project.longDescription && (
            <div className="project-slide__body">
              <p>{project.longDescription}</p>
            </div>
          )}

          {project.architecture && (
            <div className="project-slide__architecture">
              <h4 className="project-slide__architecture-heading">Architecture</h4>
              <p>{project.architecture}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDescriptionSlide;
